/** Valores por defecto de negocio (ajustar solo aquí si la marca cambia los textos). */
export const UTM_DEFAULT_SOURCE = "organico";
export const UTM_DEFAULT_MEDIUM = "landing_mkt";
export const UTM_DEFAULT_CAMPAIGN = "gac";

const hasParam = (query, key) => {
  const v = query.get(key);
  return v != null && String(v).trim() !== "";
};

const normalizeParam = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
};

/** Descarta valores legacy o no válidos de utm_source (ej. "directo", "consulta web"). */
export function sanitizeMarketingUtmSource(value) {
  const n = normalizeParam(value);
  if (!n) return null;
  const compact = n.replace(/\s+/g, " ").replace(/_/g, " ").trim();
  if (/^consulta web$/i.test(compact)) return null;
  if (compact.toLowerCase() === "directo") return null;
  return n;
}

/**
 * `true` si en la query actual faltan los tres UTMs principales
 * (`utm_source`, `utm_medium`, `utm_campaign`).
 */
export const urlSinUtmsPrincipales = (query) => {
  return (
    !hasParam(query, "utm_source") && !hasParam(query, "utm_medium") && !hasParam(query, "utm_campaign")
  );
};

const readSearchParams = () => new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");

const resolveParam = (query, key, defaultValue, sanitize) => {
  if (hasParam(query, key)) {
    const raw = query.get(key);
    if (sanitize) {
      const sanitized = sanitize(raw);
      if (sanitized) return sanitized;
    } else {
      return raw;
    }
  }
  const stored = sessionStorage.getItem(key);
  if (stored != null && String(stored).trim() !== "") {
    if (sanitize) {
      const sanitized = sanitize(stored);
      if (sanitized) return sanitized;
    } else {
      return stored;
    }
  }
  return defaultValue;
};

const resolveUtmSource = (query, defaultValue) =>
  resolveParam(query, "utm_source", defaultValue, sanitizeMarketingUtmSource) || defaultValue;

/**
 * Devuelve `{ utm_source, utm_medium, utm_campaign, utm_term }`.
 * Cada UTM: valor en la URL → sessionStorage → default (`organico` / `landing_mkt` / `gac`).
 * `utm_term` solo si viene en URL o session (sin default).
 */
export const getUtmParams = () => {
  if (typeof window === "undefined") {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: UTM_DEFAULT_CAMPAIGN,
      utm_term: undefined,
    };
  }

  const query = readSearchParams();

  if (urlSinUtmsPrincipales(query)) {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: UTM_DEFAULT_CAMPAIGN,
      utm_term: undefined,
    };
  }

  const utm_source = resolveUtmSource(query, UTM_DEFAULT_SOURCE);
  const utm_medium = resolveParam(query, "utm_medium", UTM_DEFAULT_MEDIUM) || UTM_DEFAULT_MEDIUM;
  const utm_campaign = resolveParam(query, "utm_campaign", UTM_DEFAULT_CAMPAIGN) || UTM_DEFAULT_CAMPAIGN;

  const utm_term = hasParam(query, "utm_term")
    ? query.get("utm_term")
    : (() => {
        const stored = sessionStorage.getItem("utm_term");
        return stored != null && String(stored).trim() !== "" ? stored : undefined;
      })();

  return { utm_source, utm_medium, utm_campaign, utm_term };
};

/**
 * Sin UTMs principales en la URL → guardar los tres defaults en session y limpiar `utm_term`.
 * Con UTMs en la URL → persistir en session cada param presente en query (valores no vacíos).
 */
export const saveUtmParams = () => {
  if (typeof window === "undefined") return;

  const q = readSearchParams();

  if (urlSinUtmsPrincipales(q)) {
    sessionStorage.setItem("utm_source", UTM_DEFAULT_SOURCE);
    sessionStorage.setItem("utm_medium", UTM_DEFAULT_MEDIUM);
    sessionStorage.setItem("utm_campaign", UTM_DEFAULT_CAMPAIGN);
    sessionStorage.removeItem("utm_term");
    return;
  }

  ["utm_source", "utm_medium", "utm_campaign", "utm_term"].forEach((key) => {
    const val = q.get(key);
    if (val != null && String(val).trim() !== "") {
      if (key === "utm_source") {
        const sanitized = sanitizeMarketingUtmSource(val);
        sessionStorage.setItem(key, sanitized || UTM_DEFAULT_SOURCE);
      } else {
        sessionStorage.setItem(key, val);
      }
    }
  });
};

/**
 * `window.location.href` normalizado: completa source/medium/campaign faltantes;
 * sin ningún principal en la URL → los tres defaults en search.
 */
export const getCurrentUrlWithUtmDefaults = () => {
  if (typeof window === "undefined") return "";

  const current = new URL(window.location.href);
  const q = current.searchParams;

  if (urlSinUtmsPrincipales(q)) {
    q.set("utm_source", UTM_DEFAULT_SOURCE);
    q.set("utm_medium", UTM_DEFAULT_MEDIUM);
    q.set("utm_campaign", UTM_DEFAULT_CAMPAIGN);
    q.delete("utm_term");
    current.search = q.toString();
    return current.toString();
  }

  const source = resolveUtmSource(q, UTM_DEFAULT_SOURCE);
  const medium = resolveParam(q, "utm_medium", UTM_DEFAULT_MEDIUM) || UTM_DEFAULT_MEDIUM;
  const campaign = resolveParam(q, "utm_campaign", UTM_DEFAULT_CAMPAIGN) || UTM_DEFAULT_CAMPAIGN;

  q.set("utm_source", source);
  if (!hasParam(q, "utm_medium")) q.set("utm_medium", medium);
  if (!hasParam(q, "utm_campaign")) q.set("utm_campaign", campaign);

  current.search = q.toString();
  return current.toString();
};

/** Ruta interna preservando query string actual o UTMs por defecto. */
export const buildRouteWithUtms = (route, search = "") => {
  if (search && search.length > 1) {
    return `${route}${search}`;
  }
  return `${route}${buildUtmQueryString()}`;
};

/** `?utm_source=...&utm_medium=...&utm_campaign=...` (+ utm_term si existe), usando `getUtmParams()`. */
export const buildUtmQueryString = () => {
  const { utm_source, utm_medium, utm_campaign, utm_term } = getUtmParams();
  const parts = [
    `utm_source=${encodeURIComponent(utm_source)}`,
    `utm_medium=${encodeURIComponent(utm_medium)}`,
    `utm_campaign=${encodeURIComponent(utm_campaign)}`,
  ];
  if (utm_term) parts.push(`utm_term=${encodeURIComponent(utm_term)}`);
  return `?${parts.join("&")}`;
};
